import React from 'react'
import clsx from 'clsx'
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux'
import { CircularProgress } from '@mui/material'

import MediaViewerModal from '../../../ChatModals/MediaViewerModal'
import Menu from '../Menu'

import useMediaCache from '@SonarCX/hooks/useMediaCache'
import { selectSentMedia, clearId } from '../../../../../slices/sentMedia'
import { useChatScroll } from '../../../../../providers/ChatScrollProvider'
import useStyles from './styles'

const Image = ({
  media,
  observer,
  className,
  overlay,
  number,
  setLoading,
  setResetChunks,
  isYours
}) => {
  const dispatch = useAppDispatch()
  const { getMedia, status, media: urls } = useMediaCache()
  const { ids } = useAppSelector(selectSentMedia)
  const { quickScrollToBottom } = useChatScroll()
  const [hovering, setHovering] = React.useState(false)
  const [scrolled, setScrolled] = React.useState(false)
  const [src, setSrc] = React.useState(null)
  const [modalOpen, setModalOpen] = React.useState(false)

  const classes = useStyles()

  const { id } = media

  const handleOpenFile = React.useCallback(() => {
    setModalOpen(true)
  }, [])

  // Starts fetching media only if it's in viewport of chat window
  React.useEffect(() => {
    if (id) {
      getMedia(id)
    }
  }, [id, getMedia])

  // Set media
  React.useEffect(() => {
    if (!id) {
      return
    }

    if (status[id] === 'DONE') {
      setSrc(urls[id])
    }
  }, [id, status, urls, setLoading])

  // Should update scroll after render
  React.useEffect(() => {
    if (src && !scrolled && observer?.boundingClientRect.y > 240) {
      const timer = setTimeout(() => {
        setScrolled(true)
      }, 500)

      return () => {
        clearTimeout(timer)
      }
    }
  }, [src, scrolled, observer])

  // Scroll once when this image is loaded
  React.useEffect(() => {
    if (ids.includes(id) && src) {
      dispatch(clearId(id))
      quickScrollToBottom()
    }
  }, [ids, id, src, quickScrollToBottom, dispatch])

  if (!src) {
    return (
      <div className={className}>
        <CircularProgress style={{ position: 'relative' }} />
      </div>
    )
  }

  return (
    <div
      className={className}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {overlay && (
        <div className={classes.text} onClick={() => setResetChunks(true)}>
          + {number} more
        </div>
      )}

      <img
        src={src}
        onClick={handleOpenFile}
        alt='<Media>'
        className={clsx(
          classes.root,
          className && classes.maxHeight,
          overlay && classes.opaque
        )}
      />

      {hovering && (
        <Menu
          img
          isYours={isYours}
          file={media}
          onOpenFileClick={handleOpenFile}
        />
      )}
      {modalOpen && (
        <MediaViewerModal
          file={modalOpen ? media : null}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  )
}

export default Image
